// The console's one bento grammar: level rows of cards.
//
// A band is a VizRow; its cards ask for a span of twelve (3, 4, 6, 8, 12)
// and the spans of one row always sum to twelve. From 1280 the row is twelve
// columns; from 768 to 1279 its cards sit two to a row (a card of eight or
// more, or an unpaired last card, takes the full row); below 768 one card per
// row. Every card in a row stretches to the row's height, and the card fills
// that height with its visual (`VizCard` body grows, the plot inside grows),
// never with blank space. Gap between cards equals the card padding, row and
// column, at every width (--viz-gap, --viz-pad in console.css).
import { Children, isValidElement, useId, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { motion } from "motion/react";
import { useEnterMotion } from "../motion";

/** Spans of twelve a card may ask for at 1280 and up. */
export type VizSpan = 3 | 4 | 5 | 6 | 7 | 8 | 12;

/** Columns of two each card takes at 768 to 1279, for one row's desktop spans. */
export function tabletSpans(spans: readonly number[]): (1 | 2)[] {
  const out: (1 | 2)[] = [];
  let i = 0;
  while (i < spans.length) {
    const here = spans[i]!;
    const next = spans[i + 1];
    if (here < 8 && next !== undefined && next < 8) {
      out.push(1, 1);
      i += 2;
    } else {
      out.push(2);
      i += 1;
    }
  }
  return out;
}

/** True when one row's spans fill the twelve columns exactly. */
export function rowIsFull(spans: readonly number[]): boolean {
  return spans.reduce((sum, s) => sum + s, 0) === 12;
}

export interface VizRowProps {
  /** Accessible name of the band when it is a group of its own. */
  label?: string;
  /** Cards, each with a `span` prop; the spans sum to twelve. */
  children: ReactNode;
}

type Spanned = ReactElement<{ span?: number }>;

/** One level band of cards. Throws when the spans do not fill the row, so a page can never ship a hole. */
export function VizRow({ label, children }: VizRowProps) {
  const items = Children.toArray(children).filter(isValidElement) as Spanned[];
  const spans = items.map((el) => el.props.span ?? 12);
  if (items.length > 0 && !rowIsFull(spans)) {
    throw new Error(`VizRow spans must sum to 12, got ${spans.join("+")}`);
  }
  const md = tabletSpans(spans);
  return (
    <div className="viz-row" role={label ? "group" : undefined} aria-label={label} data-cells={items.length}>
      {items.map((el, i) => (
        <div className="viz-cell" key={el.key ?? i} data-span={spans[i]} style={{ "--lg": spans[i], "--md": md[i] } as CSSProperties}>
          {el}
        </div>
      ))}
    </div>
  );
}

export interface VizBandProps {
  /** The band heading (h2), e.g. a stage or a verdict. */
  title: string;
  /** One short meta line beside the heading, e.g. "Owner: Marketing". */
  meta?: ReactNode;
  /** Status chips at the heading's end (status first). */
  status?: ReactNode;
  id?: string;
  children: ReactNode;
}

/** A titled group of rows: heading, its status, then its level rows. */
export function VizBand({ title, meta, status, id, children }: VizBandProps) {
  const headingId = `${useId()}band`;
  return (
    <section className="viz-band" aria-labelledby={headingId} id={id}>
      <div className="viz-band-head">
        <div className="viz-band-text">
          <h2 className="viz-band-title" id={headingId}>
            {title}
          </h2>
          {meta ? <p className="viz-band-meta">{meta}</p> : null}
        </div>
        {status ? <div className="viz-band-status">{status}</div> : null}
      </div>
      {children}
    </section>
  );
}

export interface VizCardProps {
  /** Span of twelve, read by the enclosing VizRow. */
  span?: VizSpan;
  /** Card title, what the visual shows. */
  title: string;
  /** One line of context under the title. */
  description?: ReactNode;
  /** One control at the head's end: a view switch. */
  controls?: ReactNode;
  /** The takeaway figure, between the head and the visual. */
  figure?: ReactNode;
  /** A line pinned under the visual. */
  footer?: ReactNode;
  headingLevel?: 2 | 3;
  /** "flush": the body runs edge to edge (rows with hairlines). */
  variant?: "default" | "flush";
  id?: string;
  /** Written as data-card, for tests and deep links. */
  cardId?: string;
  children: ReactNode;
}

/**
 * One card: title, one-line context, an optional takeaway figure, and the
 * visual, which takes every pixel the row gives the card.
 */
export function VizCard({ title, description, controls, figure, footer, headingLevel = 3, variant = "default", id, cardId, children }: VizCardProps) {
  const headingId = `${useId()}title`;
  const enter = useEnterMotion();
  const Heading = headingLevel === 2 ? "h2" : "h3";
  return (
    <motion.section className="viz-card" data-variant={variant} data-card={cardId} aria-labelledby={headingId} id={id} {...enter}>
      <div className="viz-card-head">
        <div className="viz-card-text">
          <Heading className="viz-card-title" id={headingId}>
            {title}
          </Heading>
          {description ? <p className="viz-card-desc">{description}</p> : null}
        </div>
        {controls ? <div className="viz-card-controls">{controls}</div> : null}
      </div>
      {figure ? <div className="viz-card-figure">{figure}</div> : null}
      <div className="viz-card-body">{children}</div>
      {footer ? <div className="viz-card-foot">{footer}</div> : null}
    </motion.section>
  );
}

export interface VizFigureProps {
  /** The number, already formatted with its unit. */
  value: string;
  /** What the number is, a few words. */
  label: ReactNode;
}

/** The takeaway figure of a card: one number with its unit, then a few words. */
export function VizFigure({ value, label }: VizFigureProps) {
  return (
    <p className="viz-figure">
      <span className="viz-figure-value">{value}</span>
      <span className="viz-figure-label">{label}</span>
    </p>
  );
}

export interface ChoiceCardProps {
  span?: VizSpan;
  /** The figure that leads the card, e.g. a count. */
  value: string;
  /** What the figure counts: a chip or a few words. */
  label: ReactNode;
  /** One line under it. */
  description?: ReactNode;
  pressed: boolean;
  disabled?: boolean;
  onPress: () => void;
  /** Accessible name of the button. */
  ariaLabel: string;
}

/**
 * A summary card that is also a filter: pressing it narrows the page to what
 * it counts. Selected is a full ring, never an edge.
 */
export function ChoiceCard({ value, label, description, pressed, disabled = false, onPress, ariaLabel }: ChoiceCardProps) {
  return (
    <button type="button" className="viz-card viz-choice" aria-pressed={pressed} aria-label={ariaLabel} disabled={disabled} onClick={onPress}>
      <span className="viz-choice-value">{value}</span>
      <span className="viz-choice-label">{label}</span>
      {description ? <span className="viz-choice-desc">{description}</span> : null}
    </button>
  );
}

export interface VizListProps {
  /** Accessible name of the list. */
  label: string;
  children: ReactNode;
}

/** Records in one card: rows edge to edge, split by hairlines. */
export function VizList({ label, children }: VizListProps) {
  return (
    <ul className="viz-card viz-list" aria-label={label}>
      {children}
    </ul>
  );
}

export interface VizSplitProps {
  id?: string;
  /** The reading side: title, figure, rule, actions. */
  aside: ReactNode;
  /** The visual side; it stretches to the reading side's height. Omit and the reading side takes the row. */
  visual?: ReactNode;
}

/** One record of a VizList: its reading beside its visual from a wide card, stacked below that. */
export function VizSplit({ id, aside, visual }: VizSplitProps) {
  return (
    <li className="viz-split" id={id} data-visual={visual ? "" : undefined}>
      <div className="viz-split-aside">{aside}</div>
      {visual ? <div className="viz-split-visual">{visual}</div> : null}
    </li>
  );
}
